"use client";

import { appendHostnameToRelativeUrl } from "../../../helpers/appendHostnameToRelativeUrl";
import type { FurtherResourceItem } from "../../../helpers/data/faqs/getFurtherResources";
import { getStoryblokImageUrl } from "../../../helpers/images/getStoryblokImageUrl";
import { EMPTY_LINK_URL } from "../../../helpers/resolveLinkUrl";
import { CartoonsLinkCard } from "../CartoonsLinkCard/CartoonsLinkCard";
import { InfoLinkCard } from "../InfoLinkCard/InfoLinkCard";
import { KnowledgeRail } from "../KnowledgeRail/KnowledgeRail";
import { PromotionalLinkCard } from "../PromotionalLinkCard/PromotionalLinkCard";
import { CosmosText, CosmosTitle } from "@cosmos/web-react";
import classNames from "classnames/bind";
import styles from "./FurtherResourcesSection.module.css";

const cx = classNames.bind(styles);

/**
 * Helper function to clean up URL labels by removing common prefixes
 * If the URL is relative (no domain), it appends the current hostname to it
 * @param url - The URL string to clean up
 * @returns The cleaned URL string, or empty string if input is undefined
 */
function cleanUrlLabel(url: string | undefined): string {
  if (!url) return "";

  const processedUrl = appendHostnameToRelativeUrl(url);

  return processedUrl
    .replace(/^https?:\/\//, "") // Remove http:// or https:// protocol
    .replace(/\/$/, ""); // Remove trailing slash
  // .replace(/^www\./, "");
}

export function FurtherResourcesSection({
  knowledgeRailTitle,
  furtherResourcesTitle,
  furtherResourcesDescription,
  furtherResources,
}: {
  knowledgeRailTitle?: string;
  furtherResourcesTitle?: string;
  furtherResourcesDescription?: string;
  furtherResources: FurtherResourceItem[];
}) {
  const knowledgeRails = furtherResources.filter(
    (item) => item.__typename === "KnowledgeRail",
  );

  const furtherResourcesInfoLinkCards = furtherResources.filter(
    (item) =>
      item.__typename === "InfoLinkCard" ||
      item.__typename === "CartoonsLinkCard" ||
      item.__typename === "PromotionalLinkCard",
  );

  return (
    <div className={cx("container")}>
      {knowledgeRails.length > 0 && (
        <div className={cx("knowledge-rail")}>
          {knowledgeRailTitle && (
            <CosmosText tag="h2" size="large" weight="bold" appearance="dark">
              {knowledgeRailTitle}
            </CosmosText>
          )}
          {knowledgeRails.map((resource) => (
            <KnowledgeRail
              useCrhPadding={true}
              key={resource._uuid}
              slides={resource.slides.map((slide) => ({
                _uid: slide._uid,
                title: slide.title,
                kicker: slide.kicker,
                imageUrl: slide.thumbnail.filename
                  ? getStoryblokImageUrl(slide.thumbnail.filename)
                  : "",
                linkUrl: undefined, // Intentionally no link in current design
              }))}
            />
          ))}
        </div>
      )}
      {furtherResourcesInfoLinkCards.length > 0 && (
        <div className={cx("further-resources")}>
          {(furtherResourcesTitle || furtherResourcesDescription) && (
            <div className={cx("further-resources-header")}>
              {furtherResourcesTitle && (
                <CosmosTitle tag="h2" size="small" appearance="dark">
                  {furtherResourcesTitle}
                </CosmosTitle>
              )}
              {furtherResourcesDescription && (
                <CosmosText
                  tag="p"
                  size="small"
                  appearance="dark"
                  kind="subtle"
                >
                  {furtherResourcesDescription}
                </CosmosText>
              )}
            </div>
          )}
          {furtherResourcesInfoLinkCards.map((resource) => {
            switch (resource.__typename) {
              case "PromotionalLinkCard": {
                const linkUrl = resource.promoLink?.url ?? EMPTY_LINK_URL;
                const hasLink = linkUrl !== EMPTY_LINK_URL;

                const linkLabel =
                  resource.linkLabel ||
                  (hasLink ? cleanUrlLabel(linkUrl) : "") ||
                  undefined;

                return (
                  <PromotionalLinkCard
                    key={resource._uuid}
                    kicker={resource.promoKicker ?? undefined}
                    title={resource.title}
                    linkUrl={hasLink ? linkUrl : ""}
                    linkLabel={linkLabel}
                    backgroundImageNarrowUrl={
                      resource.backgroundImageNarrow.filename
                        ? getStoryblokImageUrl(
                            resource.backgroundImageNarrow.filename,
                          )
                        : ""
                    }
                    backgroundImageWideUrl={
                      resource.backgroundImageWide.filename
                        ? getStoryblokImageUrl(
                            resource.backgroundImageWide.filename,
                          )
                        : ""
                    }
                    hasProductFlyOver={resource.hasProductFlyOver ?? undefined}
                  />
                );
              }
              case "InfoLinkCard": {
                const linkUrl = resource.link.url;
                const hasLink = linkUrl !== EMPTY_LINK_URL;

                const linkLabel =
                  resource.linkLabel ||
                  (hasLink ? cleanUrlLabel(linkUrl) : "") ||
                  undefined;

                return (
                  <InfoLinkCard
                    key={resource._uuid}
                    kicker={resource.kicker}
                    title={resource.headline}
                    linkUrl={hasLink ? linkUrl : ""}
                    linkLabel={linkLabel}
                    imageUrl={
                      resource.thumbnail.filename
                        ? getStoryblokImageUrl(resource.thumbnail.filename)
                        : ""
                    }
                    imageAlt={resource.thumbnail.alt ?? ""}
                  />
                );
              }
              case "CartoonsLinkCard": {
                const linkUrl = resource.link.url;
                const hasLink = linkUrl !== EMPTY_LINK_URL;

                const linkLabel =
                  resource.linkLabel ||
                  (hasLink ? cleanUrlLabel(linkUrl) : "") ||
                  undefined;

                return (
                  <CartoonsLinkCard
                    key={resource._uuid}
                    kicker={resource.kicker}
                    title={resource.headline}
                    linkUrl={hasLink ? linkUrl : ""}
                    linkLabel={linkLabel}
                    imageUrl={
                      resource.thumbnail.filename
                        ? getStoryblokImageUrl(resource.thumbnail.filename)
                        : ""
                    }
                    imageAlt={resource.thumbnail.alt ?? ""}
                  />
                );
              }
              default:
                return null;
            }
          })}
        </div>
      )}
    </div>
  );
}
